import React, { useState, useMemo, useCallback } from 'react';
import { Moment } from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import { getClientTagsThunk } from '@spike/marketplace-action';
import { showErrorThunk } from '@spike/notifications-action';
import { MarketplaceClientTags } from '@spike/marketplace-model';
import { OverFullWindow } from 'components/UI';
import Button from 'components/UI/V3/Button/Button';
import AlertMessage from 'components/UI/V3/AlertMessage/AlertMessage';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { DateRangeSection } from './Sections/DateRangeSection/DateRangeSection';
import { ClientTagsSection } from './Sections/ClientTagsSection/ClientTagsSection';
import { MinNightsSection } from './Sections/MinNightsSection/MinNightsSection';
import { ServicesSection, ratesConfig, BoardingServiceItem } from 'components/Boarding/Components/ServicesSection';
import { RateFormState, ValidationErrors, Rate } from './types';
import { useBoardingRates } from '../BoardingRatesProvider';
import { useApiClientWrapper } from 'hooks';
import SuccessScreen from 'components/UI/V3/SuccessScreen';
import ExitWithoutSavingModal from 'components/UI/V3/Modals/ExitWithoutSavingModal';
import styles from './RateForm.module.scss';
import { RootState } from 'store';
import useHideHubSpotChatWithPointerFix from 'hooks/useHideHubSpotChatWithPointerFix';

interface RateFormProps {
    onClose: () => void;
    rate?: Rate | null;
    serviceId?: number;
}

const RateForm: React.FC<RateFormProps> = ({ onClose, rate, serviceId }) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const {
        boardingServices,
        saveRate,
        splitAndSaveRate,
        fetchRatesByServiceId,
        isRateSharedWithOtherServices,
        areAllServiceRatesLoaded,
        marketplaceId,
        isLoading
    } = useBoardingRates();

    useHideHubSpotChatWithPointerFix(true);

    // Fetch fresh client tags on mount
    React.useEffect(() => {
        dispatch(getClientTagsThunk(apiClientWrapper));
    }, []);

    const clientTagsStore = useSelector<RootState, MarketplaceClientTags>(state => state.marketplace.clientTags);
    const availableClientTags = useMemo(() => {
        return [...clientTagsStore.positive]
            .sort((a, b) => {
                const sourceOrderA = a.source === 'default' ? 0 : 1;
                const sourceOrderB = b.source === 'default' ? 0 : 1;
                if (sourceOrderA !== sourceOrderB) return sourceOrderA - sourceOrderB;
                return a.name.localeCompare(b.name);
            })
            .map(tag => ({ id: String(tag.id), name: tag.name }));
    }, [clientTagsStore.positive]);

    // Single initialization - services are enriched immediately in fromRate()
    // No separate enrichment effect needed!
    const [formState, setFormState] = useState<RateFormState>(() => {
        if (rate && boardingServices.length > 0) {
            return RateFormState.fromRate(rate, boardingServices, serviceId);
        }
        return RateFormState.createEmpty();
    });

    const [errors, setErrors] = useState<ValidationErrors>({});
    const [showExitPrompt, setShowExitPrompt] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    // Validation as computed value - returns only boolean for button state
    const validateForm = useMemo((): boolean => {
        const newErrors: ValidationErrors = {};

        if (!formState.startDate) {
            newErrors.startDate = 'Start date is required';
        }

        if (!formState.endDate) {
            newErrors.endDate = 'End date is required';
        }

        if (formState.startDate && formState.endDate && formState.endDate.isBefore(formState.startDate)) {
            newErrors.endDate = 'End date must be after start date';
        }

        if (formState.services.length === 0) {
            newErrors.services = 'At least one service is required';
        } else if (formState.services.some(s => s.serviceId === 0 || (s.newRate ?? 0) <= 0)) {
            newErrors.services = 'Each service must be selected and have a rate greater than 0';
        }

        if (formState.minNights < 1) {
            newErrors.minNights = 'Minimum nights must be at least 1';
        }

        if (formState.clientTagsEnabled && formState.selectedTags.length === 0) {
            newErrors.clientTags = 'At least one client tag must be selected';
        }

        return Object.keys(newErrors).length === 0;
    }, [formState]);

    // Handlers use immutable updates via .with()
    const handleDateChange = useCallback((dates: { startDate: Moment | null; endDate: Moment | null }) => {
        const newStartDate = dates.startDate ? dates.startDate.clone().utc().startOf('day') : null;
        const newEndDate = dates.endDate ? dates.endDate.clone().utc().endOf('day') : null;
        setFormState(prev => prev.with({ startDate: newStartDate, endDate: newEndDate }));
        setErrors(prev => ({ ...prev, startDate: undefined, endDate: undefined }));
        setSaveError(null);
    }, []);

    const handleClientTagsChange = useCallback((data: { enabled: boolean; tags: string[] }) => {
        setFormState(prev =>
            prev.with({
                clientTagsEnabled: data.enabled,
                selectedTags: data.tags
            })
        );
        setSaveError(null);
    }, []);

    const handleMinNightsChange = useCallback((value: number) => {
        setFormState(prev => prev.with({ minNights: value }));
        setErrors(prev => ({ ...prev, minNights: undefined }));
    }, []);

    const handleServicesChange = useCallback((services: BoardingServiceItem[]) => {
        setFormState(prev => prev.with({ services }));
        setErrors(prev => ({ ...prev, services: undefined }));
        setSaveError(null);
    }, []);

    const handleSave = useCallback(async () => {
        if (!validateForm) return;

        setSaveError(null);

        const needsSplit =
            formState.isEditMode &&
            serviceId !== undefined &&
            formState.hasRateLevelChanges() &&
            isRateSharedWithOtherServices(formState.originalId!, serviceId);

        // Collect all service IDs that need refreshing after the operation
        const serviceIdsToRefresh = new Set(formState.services.map(s => s.serviceId));
        // Also include the originating service card in case the user removed it from the form
        if (serviceId !== undefined) serviceIdsToRefresh.add(serviceId);

        try {
            if (needsSplit) {
                const createPayload = formState.toCreateSplitRate(marketplaceId);
                const destroyPayload = formState.toDestroyServiceItemRate(marketplaceId);
                // splitAndSaveRate already refreshes serviceId; refresh remaining ones in parallel
                const remainingIds = Array.from(serviceIdsToRefresh).filter(id => id !== serviceId);
                await Promise.all([
                    splitAndSaveRate(createPayload, destroyPayload, serviceId!),
                    ...remainingIds.map(id => fetchRatesByServiceId(id))
                ]);
            } else {
                await saveRate(formState.toSaveRate(marketplaceId));
                await Promise.all(Array.from(serviceIdsToRefresh).map(id => fetchRatesByServiceId(id)));
            }

            setIsSuccess(true);
        } catch (err: unknown) {
            const responseData = (err as { response?: { data?: Record<string, unknown> } })?.response?.data;
            if (responseData && typeof responseData === 'object') {
                if (Array.isArray(responseData.start_date)) {
                    setSaveError(
                        'Another rate is set for the same time period for one or more of these services and/or client tags. Please update the dates to avoid conflicts.'
                    );
                } else if (Array.isArray(responseData.name)) {
                    dispatch(showErrorThunk(`Rate name ${(responseData.name as string[])[0]}`));
                }
            }
        }
    }, [
        validateForm,
        formState,
        marketplaceId,
        serviceId,
        saveRate,
        splitAndSaveRate,
        fetchRatesByServiceId,
        isRateSharedWithOtherServices
    ]);

    const handleClose = useCallback(() => {
        setShowExitPrompt(true);
    }, []);

    const getTitle = () => {
        return formState.isEditMode ? 'Edit Rate' : 'Add Rate';
    };

    const getSuccessMessage = () => {
        const serviceNames = formState.services
            .map(s => s.serviceName)
            .filter(Boolean)
            .join(', ');

        return formState.isEditMode ? (
            <span>Rate for selected boarding service(s) has been successfully updated.</span>
        ) : (
            <span>Rate for selected boarding service(s) has been created and is now active.</span>
        );
    };

    const getSuccessButtonLabel = () => {
        return 'Return to Rates';
    };

    // Success screen
    if (isSuccess) {
        return (
            <OverFullWindow
                showMobileLogo
                title={getTitle()}
                onClose={onClose}
            >
                <SuccessScreen
                    title={'Good Job'}
                    message={getSuccessMessage()}
                    buttonLabel={getSuccessButtonLabel()}
                    onClick={onClose}
                    className={styles.successScreen}
                />
            </OverFullWindow>
        );
    }

    return (
        <OverFullWindow
            showMobileLogo
            title={getTitle()}
            onClose={handleClose}
        >
            {showExitPrompt && (
                <ExitWithoutSavingModal
                    onDiscard={onClose}
                    onKeepEditing={() => setShowExitPrompt(false)}
                />
            )}

            <div className={styles.container}>
                <form
                    className={styles.form}
                    onSubmit={e => e.preventDefault()}
                >
                    <DateRangeSection
                        startDate={formState.startDate}
                        endDate={formState.endDate}
                        onChange={handleDateChange}
                        errors={errors}
                    />

                    <ClientTagsSection
                        enabled={formState.clientTagsEnabled}
                        tags={formState.selectedTags}
                        availableTags={availableClientTags}
                        onChange={handleClientTagsChange}
                    />

                    <MinNightsSection
                        value={formState.minNights}
                        onChange={handleMinNightsChange}
                        error={errors.minNights}
                    />

                    <ServicesSection
                        services={formState.services}
                        availableServices={boardingServices}
                        onChange={handleServicesChange}
                        error={errors.services}
                        isLoading={formState.isEditMode && serviceId !== undefined && !areAllServiceRatesLoaded}
                        config={ratesConfig}
                    />

                    <div className={styles.actions}>
                        {saveError && (
                            <AlertMessage
                                icon={faTriangleExclamation}
                                bgColor="#F5F0E8"
                                bdColor="#D4C5A9"
                                iconColor="#C27803"
                                className={styles.saveError}
                            >
                                {saveError}
                            </AlertMessage>
                        )}
                        <Button
                            label="Save"
                            variant="primary"
                            size="large"
                            onClick={handleSave}
                            disabled={!validateForm || !!saveError}
                            isLoading={isLoading}
                            noColorOnDisabled={true}
                        />
                    </div>
                </form>
            </div>
        </OverFullWindow>
    );
};

export default RateForm;
